/** Shared field eligibility for board display controls and the server's bounded presentation projection. */
import { MetadataEntityFieldData, MetadataEntityFieldEditorType, MetadataEntityFieldType, MetadataEntityRecordExtendedScope, MetadataEntityRecordScope } from '../../../metadata/entity/decorators/model';

/** Board field pickers and projection include the rendered grain and its ancestors, never deeper descendants. */
export function isFieldScopeWithin(fieldScope: MetadataEntityRecordExtendedScope | null | undefined, boardScope: MetadataEntityRecordExtendedScope): boolean {

  const scope = fieldScope ?? MetadataEntityRecordScope.Any;
  if (scope === MetadataEntityRecordScope.Any || boardScope === MetadataEntityRecordScope.Any) return true;
  if (boardScope === MetadataEntityRecordScope.Primary) return scope === MetadataEntityRecordScope.Primary;
  if (scope === MetadataEntityRecordScope.Primary || scope === MetadataEntityRecordScope.Secondary) return true;
  if (boardScope === MetadataEntityRecordScope.Secondary) return false;
  return boardScope === scope || boardScope.startsWith(`${scope}.`);
}

/** Board field controls and server projection share these exclusions for hidden fields and long-form notes. */
export function isBoardDisplayField(field: MetadataEntityFieldData): boolean {

  if (!field || field.hidden) return false;
  if (field.editor === MetadataEntityFieldEditorType.TextArea || field.editor === MetadataEntityFieldEditorType.RichText || field.editor === MetadataEntityFieldEditorType.MergeInput) return false;
  return true;
}

/** List controls and server projection share this metadata-defined chip pool; hidden and note fields are never candidates. */
export function isListDisplayPropertyField(field: MetadataEntityFieldData): boolean {

  if (!isBoardDisplayField(field)) return false;
  const fieldType = displayPropertyType(field);
  const visualType = field.visualType;
  if (visualType === 'status' || visualType === 'color' || visualType === 'completion') return true;
  if (field.identifier) return true;

  switch (fieldType) {
    case MetadataEntityFieldType.Enum:
    case MetadataEntityFieldType.Tag:
    case MetadataEntityFieldType.Lookup:
    case MetadataEntityFieldType.Parent:
    case MetadataEntityFieldType.Date:
    case MetadataEntityFieldType.DateTime:
    case MetadataEntityFieldType.Time:
    case MetadataEntityFieldType.Duration:
    case MetadataEntityFieldType.Number:
    case MetadataEntityFieldType.Percentage:
    case MetadataEntityFieldType.Currency:
    case MetadataEntityFieldType.Boolean:
      return true;
    default:
      return false;
  }
}

/** Gantt column controls and projection admit display values, never whole embedded documents or arbitrary custom objects. */
export function isGanttColumnField(field: MetadataEntityFieldData): boolean {

  if (!isBoardDisplayField(field)) return false;
  if (isListDisplayPropertyField(field)) return true;
  switch (displayPropertyType(field)) {
    case MetadataEntityFieldType.String:
    case MetadataEntityFieldType.StringAlphaNumeric:
    case MetadataEntityFieldType.StringAlpha:
    case MetadataEntityFieldType.Phone:
    case MetadataEntityFieldType.Email:
    case MetadataEntityFieldType.Url:
    case MetadataEntityFieldType.Flag:
    case MetadataEntityFieldType.Site:
    case MetadataEntityFieldType.Address:
    case MetadataEntityFieldType.EntityStatus:
      return true;
    default:
      return false;
  }
}

function displayPropertyType(field: MetadataEntityFieldData): MetadataEntityFieldType {

  const type = field.type === MetadataEntityFieldType.CustomField ? field.typeData?.type : field.type;
  return Array.isArray(type) ? type[0] : type;
}
